/**
 * PaymentLedgerPage — shared page for both Program Assessment and Retention
 * Fee modules. Chooses category + display config via the `mode` prop.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, GraduationCap, Wallet, Search, AlertCircle, Trash2 } from "lucide-react";
import PaymentDialog from "@/components/PaymentDialog";

const MODES = {
  program_assessment: {
    title: "Program Assessment",
    subtitle: "Record payments for program assessments. Applicants who are not yet enrolled can be entered by name.",
    icon: GraduationCap,
    color: "#7C3AED",
    accentBg: "#F5EDFF",
    allowUnenrolled: true,
    dialogTitle: "Record Program Assessment Payment",
    dialogDesc: "The payment will automatically post to Income and the student's account.",
    permissionKey: "program_assessment_view",
    createPermKey: "program_assessment_create",
    deletePermKey: "program_assessment_delete",
  },
  retention_fee: {
    title: "Retention Fee",
    subtitle: "Record retention-fee payments from enrolled students.",
    icon: Wallet,
    color: "#0F766E",
    accentBg: "#DBFCE7",
    allowUnenrolled: false,
    dialogTitle: "Record Retention Fee Payment",
    dialogDesc: "The payment will automatically post to Income and the student's account.",
    permissionKey: "retention_fee_view",
    createPermKey: "retention_fee_create",
    deletePermKey: "retention_fee_delete",
  },
};

const currencyOf = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PaymentLedgerPage({ mode }) {
  const cfg = MODES[mode];
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const branchId = selectedBranch?.id;
  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";
  const perms = user?.permissions || {};
  const canCreate = isOwner || !!perms[cfg.createPermKey];
  const canDelete = isOwner || !!perms[cfg.deletePermKey];

  const [records, setRecords] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    if (!branchId) { setRecords([]); return; }
    setLoading(true);
    try {
      const [rec, stu] = await Promise.all([
        api.get("/student-accounts", { params: { branch_id: branchId, category: mode } }),
        api.get("/students", { params: { branch_id: branchId } }),
      ]);
      setRecords(rec.data?.records || rec.data || []);
      setStudents(stu.data?.items || stu.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }, [branchId, mode]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;
    return records.filter((r) => (r.student_name || "").toLowerCase().includes(q));
  }, [records, search]);

  const total = useMemo(() => filtered.reduce((sum, r) => sum + Number(r.paid_amount || 0), 0), [filtered]);

  async function deleteRecord(rec) {
    if (!window.confirm(`Delete this ${cfg.title.toLowerCase()} payment? This cannot be undone.`)) return;
    try {
      await api.delete(`/student-accounts/${rec.id}`);
      toast.success("Payment removed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  if (!branchId) {
    return (
      <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 max-w-xl">
        <AlertCircle className="w-10 h-10 text-brand-purple/40 mb-2" />
        <p className="font-heading font-semibold text-lg">Pick a branch first</p>
        <p className="text-brand-inkMuted text-sm mt-1">{cfg.title} payments are scoped per branch.</p>
      </div>
    );
  }

  const Icon = cfg.icon;

  return (
    <div className="space-y-6" data-testid={`page-${mode}`}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: cfg.accentBg }}>
            <Icon className="w-6 h-6" style={{ color: cfg.color }} />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight">{cfg.title}</h1>
            <p className="text-brand-inkMuted text-sm mt-1 max-w-2xl">{cfg.subtitle}</p>
          </div>
        </div>
        {canCreate && (
          <Button
            onClick={() => setDialogOpen(true)}
            className="rounded-full px-5 h-11 text-white shadow-floating"
            style={{ background: cfg.color }}
            data-testid={`${mode}-add-btn`}
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add {cfg.title} Payment
          </Button>
        )}
      </div>

      {/* Stats + search */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4">
          <div className="text-[10px] text-brand-inkMuted uppercase tracking-wider">Payments Recorded</div>
          <div className="font-heading text-2xl font-bold tabular-nums">{filtered.length}</div>
        </div>
        <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4">
          <div className="text-[10px] text-brand-inkMuted uppercase tracking-wider">Total Collected</div>
          <div className="font-heading text-2xl font-bold tabular-nums" style={{ color: cfg.color }}>
            ₱{currencyOf(total)}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4">
          <div className="text-[10px] text-brand-inkMuted uppercase tracking-wider mb-1">Search by student</div>
          <div className="relative">
            <Search className="w-4 h-4 text-brand-inkMuted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type student name..."
              className="rounded-xl h-9 pl-9"
              data-testid={`${mode}-search`}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-brand-inkMuted">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid={`${mode}-empty`}>
            <Icon className="w-12 h-12 mx-auto mb-2" style={{ color: cfg.color, opacity: 0.4 }} />
            <p className="font-heading font-semibold">No {cfg.title.toLowerCase()} payments yet</p>
            <p className="text-brand-inkMuted text-sm mt-1">Click &quot;Add {cfg.title} Payment&quot; to record the first one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FE] text-brand-inkMuted">
                <tr>
                  <th className="text-left px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider">DATE</th>
                  <th className="text-left px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider">STUDENT</th>
                  <th className="text-left px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider">METHOD</th>
                  <th className="text-left px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider">REF NO.</th>
                  <th className="text-right px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider">AMOUNT</th>
                  <th className="text-left px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider">NOTES</th>
                  {canDelete && <th className="w-10"></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const pay = (r.payments || [])[0] || {};
                  return (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`${mode}-row-${r.id}`}>
                      <td className="px-3 py-2.5 whitespace-nowrap tabular-nums text-brand-inkMuted">{r.date || (r.created_at || "").slice(0, 10)}</td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-foreground">
                          {r.student_name || <span className="italic text-brand-inkMuted">Unknown</span>}
                          {r.is_unenrolled_applicant && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800">Applicant</span>
                          )}
                        </div>
                        {(r.student_class_name || r.student_section_name) && (
                          <div className="text-xs text-brand-inkMuted">
                            {[r.student_class_name, r.student_section_name].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 capitalize">{(pay.mode || "cash").replace("_", " ")}</td>
                      <td className="px-3 py-2.5 text-brand-inkMuted">{pay.reference_no || "—"}</td>
                      <td className="px-3 py-2.5 text-right font-semibold tabular-nums" style={{ color: cfg.color }}>₱{currencyOf(r.paid_amount)}</td>
                      <td className="px-3 py-2.5 text-brand-inkMuted max-w-xs truncate">{r.notes || "—"}</td>
                      {canDelete && (
                        <td className="px-2 py-2 text-right">
                          <button
                            onClick={() => deleteRecord(r)}
                            className="text-brand-inkMuted hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                            aria-label="Delete payment"
                            data-testid={`${mode}-delete-${r.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PaymentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => { setDialogOpen(false); load(); }}
        category={mode}
        branchId={branchId}
        students={students}
        allowUnenrolled={cfg.allowUnenrolled}
        title={cfg.dialogTitle}
        description={cfg.dialogDesc}
      />
    </div>
  );
}
